import React, { useMemo } from 'react';

/**
 * Pure-SVG radar chart. No dependencies.
 *
 * categories: [{name, percent, score, max, completed, total}]
 * size     : svg viewbox size (square). Default 320.
 */
const SkillRadarChart = ({ categories = [], size = 320, color = '#00FF00' }) => {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.38;

  const points = useMemo(() => {
    const n = Math.max(categories.length, 3);
    return categories.map((c, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const pct = Math.min(Math.max(Number(c.percent) || 0, 0), 100) / 100;
      const rr = radius * pct;
      return {
        name: c.name,
        pct,
        x: cx + Math.cos(angle) * rr,
        y: cy + Math.sin(angle) * rr,
        axisX: cx + Math.cos(angle) * radius,
        axisY: cy + Math.sin(angle) * radius,
        labelX: cx + Math.cos(angle) * (radius + 22),
        labelY: cy + Math.sin(angle) * (radius + 22),
      };
    });
  }, [categories, cx, cy, radius]);

  const polygonPath = points.map((p) => `${p.x},${p.y}`).join(' ');
  const rings = [0.25, 0.5, 0.75, 1];

  if (categories.length < 3) {
    return (
      <div
        className="flex items-center justify-center text-sm text-gray-500"
        style={{ height: size }}
        data-testid="skill-radar-empty"
      >
        Complete rooms in at least 3 categories to unlock your skill radar.
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      data-testid="skill-radar-svg"
      role="img"
      aria-label="Skill radar chart"
    >
      {/* Concentric rings */}
      {rings.map((r) => (
        <circle
          key={r}
          cx={cx}
          cy={cy}
          r={radius * r}
          fill="none"
          stroke="#2A2A2A"
          strokeWidth={1}
        />
      ))}

      {/* Axes */}
      {points.map((p) => (
        <line
          key={`axis-${p.name}`}
          x1={cx}
          y1={cy}
          x2={p.axisX}
          y2={p.axisY}
          stroke="#2A2A2A"
          strokeWidth={1}
        />
      ))}

      {/* Score polygon */}
      <polygon
        points={polygonPath}
        fill={`${color}33`}
        stroke={color}
        strokeWidth={2}
      />

      {/* Vertex dots */}
      {points.map((p) => (
        <circle key={`dot-${p.name}`} cx={p.x} cy={p.y} r={3} fill={color} />
      ))}

      {/* Labels */}
      {points.map((p) => {
        // align label by angle
        const dx = p.labelX - cx;
        // Flattened label-anchor helper (was a nested ternary on one line).
        let anchor;
        if (Math.abs(dx) < 8) anchor = 'middle';
        else if (dx > 0) anchor = 'start';
        else anchor = 'end';
        return (
          <text
            key={`label-${p.name}`}
            x={p.labelX}
            y={p.labelY}
            fill="#E5E5E5"
            fontSize={11}
            fontFamily="ui-monospace, SFMono-Regular, monospace"
            textAnchor={anchor}
            dominantBaseline="middle"
          >
            {p.name}
          </text>
        );
      })}
    </svg>
  );
};

export default SkillRadarChart;
